import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import AIPlatformTester from '@/components/agentic/AIPlatformTester';
import CapabilityManager from '@/components/agentic/CapabilityManager';
import CodeAnalyzer from '@/components/agentic/sparc/CodeAnalyzer';
import SparcLearningGuide from '@/components/agentic/sparc/SparcLearningGuide';
import AgenticAgentsWrapper from '@/components/agentic/agents/AgenticAgentsWrapper';
import { useSparcAgent } from '@/hooks/sparc/useSparcAgent';
import { toast } from 'sonner';
import { 
  GitBranch, 
  Layers, 
  Blocks, 
  Code, 
  BookOpen, 
  Workflow, 
  Zap, 
  Brain, 
  ShieldCheck,
  History,
  RotateCcw,
  Loader2,
  AlertCircle,
  Check,
  FileText
} from 'lucide-react';

const AgenticDashboard = () => {
  const [activeTab, setActiveTab] = useState('test');
  const [checkpointName, setCheckpointName] = useState('');
  const [rollbackCommit, setRollbackCommit] = useState('');
  const [checkpointHistory, setCheckpointHistory] = useState<{commit: string, message: string, timestamp: string, author: string}[]>([]);
  const [showCheckpointSuccess, setShowCheckpointSuccess] = useState(false);
  const [showRollbackSuccess, setShowRollbackSuccess] = useState(false);

  const { 
    loading,
    initialized,
    error,
    checkpoint,
    rollback
  } = useSparcAgent();

  useEffect(() => {
    setCheckpointHistory([
      {
        commit: 'sparc-a1b2c3d',
        message: 'Implemented user authentication',
        timestamp: '2024-05-15T14:30:00Z',
        author: 'Agentic AI'
      },
      {
        commit: 'sparc-e5f6g7h',
        message: 'Added dashboard analytics',
        timestamp: '2024-05-14T10:15:00Z',
        author: 'Agentic AI'
      },
      {
        commit: 'sparc-i9j0k1l',
        message: 'Fixed navigation bug',
        timestamp: '2024-05-13T16:45:00Z',
        author: 'Agentic AI'
      }
    ]);
  }, []);

  const handleCreateCheckpoint = async () => {
    if (!checkpointName.trim()) {
      toast.error('Please enter a checkpoint name');
      return;
    }

    try {
      const result = await checkpoint(checkpointName);
      
      setCheckpointHistory(prev => [
        {
          commit: result.commit,
          message: result.message,
          timestamp: result.timestamp,
          author: result.author
        },
        ...prev
      ]);
      
      setShowCheckpointSuccess(true);
      setTimeout(() => setShowCheckpointSuccess(false), 5000);
      
      setCheckpointName('');
      
      toast.success('Checkpoint created successfully');
    } catch (error) {
      toast.error(`Failed to create checkpoint: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleRollback = async () => {
    if (!rollbackCommit.trim()) {
      toast.error('Please enter a commit hash');
      return;
    }

    try {
      const result = await rollback(rollbackCommit);
      
      if (result.success) {
        setShowRollbackSuccess(true);
        setTimeout(() => setShowRollbackSuccess(false), 5000);
        
        setRollbackCommit('');
        
        toast.success('Rolled back successfully');
      } else {
        toast.error(`Rollback failed: ${result.message}`);
      }
    } catch (error) {
      toast.error(`Failed to rollback: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Agentic AI Tools Dashboard</h1>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap gap-2">
          <TabsTrigger value="test" className="flex items-center">
            <Zap className="mr-2 h-4 w-4" />
            AI Testing
          </TabsTrigger>
          <TabsTrigger value="platform-agents" className="flex items-center">
            <Brain className="mr-2 h-4 w-4" />
            Platform Agents
          </TabsTrigger>
          <TabsTrigger value="agents" className="flex items-center">
            <Blocks className="mr-2 h-4 w-4" />
            Agents
          </TabsTrigger>
          <TabsTrigger value="capabilities" className="flex items-center">
            <Layers className="mr-2 h-4 w-4" />
            Capabilities
          </TabsTrigger>
          <TabsTrigger value="workflows" className="flex items-center">
            <Workflow className="mr-2 h-4 w-4" />
            Workflows
          </TabsTrigger>
          <TabsTrigger value="code-analysis" className="flex items-center">
            <Code className="mr-2 h-4 w-4" />
            Code Analysis
          </TabsTrigger>
          <TabsTrigger value="version-control" className="flex items-center">
            <GitBranch className="mr-2 h-4 w-4" />
            Version Control
          </TabsTrigger>
          <TabsTrigger value="learning" className="flex items-center">
            <BookOpen className="mr-2 h-4 w-4" />
            Learning
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="test" className="space-y-6">
          <AIPlatformTester />
        </TabsContent>
        
        <TabsContent value="platform-agents" className="space-y-6">
          <AgenticAgentsWrapper />
        </TabsContent>
        
        <TabsContent value="agents" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Agent Management</CardTitle>
            </CardHeader>
            <CardContent>
              <p>Agent configuration and management tools coming soon!</p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="capabilities" className="space-y-6">
          <CapabilityManager />
        </TabsContent>
        
        <TabsContent value="workflows" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Agentic Workflows</CardTitle>
            </CardHeader>
            <CardContent>
              <p>Workflow definition and execution tools coming soon!</p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="code-analysis" className="space-y-6">
          <CodeAnalyzer />
        </TabsContent>
        
        <TabsContent value="version-control" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <GitBranch className="mr-2 h-5 w-5 text-primary" />
                SPARC Version Control
              </CardTitle>
              <CardDescription>
                Create checkpoints before making significant changes and roll back if needed.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!initialized ? (
                <div className="flex items-center justify-center p-6">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
                    <p>Initializing SPARC version control...</p>
                  </div>
                </div>
              ) : (
                <>
                  {showCheckpointSuccess && (
                    <Alert className="mb-4 bg-green-50 border-green-200">
                      <Check className="h-4 w-4 text-green-600" />
                      <AlertTitle className="text-green-600">Success</AlertTitle>
                      <AlertDescription className="text-green-600">
                        Checkpoint created successfully.
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {showRollbackSuccess && (
                    <Alert className="mb-4 bg-green-50 border-green-200">
                      <Check className="h-4 w-4 text-green-600" />
                      <AlertTitle className="text-green-600">Success</AlertTitle>
                      <AlertDescription className="text-green-600">
                        Successfully rolled back to previous checkpoint.
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {error && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Error</AlertTitle>
                      <AlertDescription>
                        {error.message}
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Create Checkpoint</h3>
                      <p className="text-sm text-muted-foreground">
                        Save the current state of your code with a descriptive message.
                      </p>
                      <div className="flex items-center space-x-2">
                        <Input
                          placeholder="Checkpoint message"
                          value={checkpointName}
                          onChange={(e) => setCheckpointName(e.target.value)}
                          disabled={loading}
                        />
                        <Button 
                          onClick={handleCreateCheckpoint}
                          disabled={loading || !checkpointName.trim()}
                        >
                          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                          Create
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Rollback to Checkpoint</h3>
                      <p className="text-sm text-muted-foreground">
                        Revert to a previous code state using a commit hash.
                      </p>
                      <div className="flex items-center space-x-2">
                        <Input
                          placeholder="Commit hash"
                          value={rollbackCommit}
                          onChange={(e) => setRollbackCommit(e.target.value)}
                          disabled={loading}
                        />
                        <Button 
                          variant="secondary"
                          onClick={handleRollback}
                          disabled={loading || !rollbackCommit.trim()}
                        >
                          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                          Rollback
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <Separator className="my-6" />
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium">Checkpoint History</h3>
                      <History className="h-5 w-5 text-muted-foreground" />
                    </div>
                    
                    <div className="space-y-2">
                      {checkpointHistory.map((item, index) => (
                        <div 
                          key={index}
                          className="flex items-center justify-between p-3 rounded-md border hover:bg-accent"
                        >
                          <div className="space-y-1">
                            <div className="font-medium">{item.message}</div>
                            <div className="text-sm text-muted-foreground">
                              {new Date(item.timestamp).toLocaleString()} by {item.author}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <code className="px-2 py-1 bg-muted rounded text-sm">{item.commit}</code>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              title="Rollback to this checkpoint"
                              onClick={() => {
                                setRollbackCommit(item.commit);
                                handleRollback();
                              }}
                              disabled={loading}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="learning" className="space-y-6">
          <SparcLearningGuide />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AgenticDashboard;
